import React, { useEffect } from 'react';

const ReadAloud: React.FC = () => {
  useEffect(() => {
    let popup: HTMLButtonElement | null = null;
    let lastX = 0;
    let lastY = 0;
    const attachedDocuments = new Set<Document>();
    const iframeLoadHandlers = new Map<HTMLIFrameElement, () => void>();
    const iframeDocumentCleanups = new Map<HTMLIFrameElement, () => void>();
    const trackedIframes = new Set<HTMLIFrameElement>();
    const documentCleanups = new Map<Document, () => void>();
    const selectionTimers: number[] = [];
    const isAppleTouchDevice =
      navigator.maxTouchPoints > 1 && /Mac|iPad|iPhone/.test(navigator.platform);

    const hidePopup = () => {
      if (popup) popup.style.display = 'none';
    };

    const stopSpeech = () => {
      window.speechSynthesis?.cancel();
    };

    const createPopup = () => {
      if (popup) return;

      popup = document.createElement('button');
      popup.type = 'button';
      popup.id = 'readPopup';
      popup.setAttribute('aria-label', 'Read selected text aloud');
      popup.innerHTML = '<span aria-hidden="true">🔊</span> Read Aloud';
      Object.assign(popup.style, {
        position: 'absolute',
        display: 'none',
        zIndex: '999999',
        padding: isAppleTouchDevice ? '0 20px' : '10px 15px',
        width: isAppleTouchDevice ? '170px' : 'auto',
        height: isAppleTouchDevice ? '60px' : 'auto',
        border: '0',
        borderRadius: isAppleTouchDevice ? '30px' : '8px',
        background: '#111',
        color: '#fff',
        cursor: 'pointer',
        fontFamily: 'Arial, sans-serif',
        fontSize: isAppleTouchDevice ? '20px' : '14px',
        boxShadow: '0 6px 20px rgba(0,0,0,.25)',
        alignItems: 'center',
        justifyContent: 'center',
      });

      popup.addEventListener('click', (event) => {
        event.stopPropagation();
        const selectedText = popup?.dataset.text;
        if (!selectedText || !window.speechSynthesis) return;

        window.speechSynthesis.cancel();
        const speech = new SpeechSynthesisUtterance(selectedText);
        speech.lang = 'en-US';
        speech.rate = 1;
        speech.pitch = 1;
        window.speechSynthesis.speak(speech);
        hidePopup();
      });

      document.body.appendChild(popup);
    };

    const showPopup = (text: string, rect: DOMRect, iframe?: HTMLIFrameElement) => {
      createPopup();
      if (!popup) return;

      const iframeRect = iframe?.getBoundingClientRect();
      const left = iframeRect
        ? iframeRect.left + window.scrollX + rect.left + rect.width / 2 - 50
        : rect.left + window.scrollX + rect.width / 2 - 50;
      const top = iframeRect
        ? iframeRect.top + window.scrollY + rect.bottom + 40
        : rect.top + window.scrollY + rect.height + 40;

      popup.dataset.text = text;
      popup.style.left = `${left}px`;
      popup.style.top = `${top}px`;
      popup.style.display = isAppleTouchDevice ? 'flex' : 'block';
    };

    const processSelection = (ownerDocument: Document, iframe?: HTMLIFrameElement) => {
      const activeElement = ownerDocument.activeElement;
      let text = '';
      let rect: DOMRect | null = null;

      if (activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA') {
        const textField = activeElement as HTMLInputElement | HTMLTextAreaElement;
        text = textField.value
          .substring(textField.selectionStart ?? 0, textField.selectionEnd ?? 0)
          .trim();
        rect = new DOMRect(lastX, lastY, 0, 0);
      } else {
        const selection = ownerDocument.defaultView?.getSelection();
        text = selection?.toString().trim() ?? '';
        if (selection && selection.rangeCount > 0) {
          rect = selection.getRangeAt(0).getBoundingClientRect();
        }
      }

      if (!text || !rect) {
        hidePopup();
        return;
      }

      showPopup(text, rect, iframe);
    };

    const attachDocument = (ownerDocument: Document, iframe?: HTMLIFrameElement) => {
      if (!ownerDocument.body || attachedDocuments.has(ownerDocument)) return;
      attachedDocuments.add(ownerDocument);

      const handleMouseUp = (event: MouseEvent) => {
        lastX = event.clientX;
        lastY = event.clientY;
        selectionTimers.push(window.setTimeout(() => processSelection(ownerDocument, iframe), 10));
      };
      const handleTouchEnd = (event: TouchEvent) => {
        const touch = event.changedTouches[0];
        if (touch) {
          lastX = touch.clientX;
          lastY = touch.clientY;
        }
        selectionTimers.push(window.setTimeout(() => processSelection(ownerDocument, iframe), 300));
      };
      const dismiss = (event: MouseEvent | TouchEvent) => {
        if ((event.target as HTMLElement)?.id !== 'readPopup') {
          hidePopup();
          stopSpeech();
        }
      };

      ownerDocument.addEventListener('mouseup', handleMouseUp);
      ownerDocument.addEventListener('touchend', handleTouchEnd);
      ownerDocument.addEventListener('mousedown', dismiss);
      ownerDocument.addEventListener('touchstart', dismiss);

      const cleanup = () => {
        ownerDocument.removeEventListener('mouseup', handleMouseUp);
        ownerDocument.removeEventListener('touchend', handleTouchEnd);
        ownerDocument.removeEventListener('mousedown', dismiss);
        ownerDocument.removeEventListener('touchstart', dismiss);
        attachedDocuments.delete(ownerDocument);
      };
      documentCleanups.set(ownerDocument, cleanup);
      if (iframe) iframeDocumentCleanups.set(iframe, cleanup);
    };

    const attachIframes = () => {
      document.querySelectorAll('iframe').forEach((iframe) => {
        if (iframe.id === 'iframeTut' || trackedIframes.has(iframe)) return;
        trackedIframes.add(iframe);
        const loadHandler = () => {
          try {
            iframeDocumentCleanups.get(iframe)?.();
            if (iframe.contentDocument) attachDocument(iframe.contentDocument, iframe);
          } catch {
            // Cross-origin iframes cannot expose their selected text.
          }
        };
        iframe.addEventListener('load', loadHandler);
        iframeLoadHandlers.set(iframe, loadHandler);
        loadHandler();
      });
    };

    const dismissParent = (event: MouseEvent | TouchEvent) => {
      if ((event.target as HTMLElement)?.id !== 'readPopup') {
        hidePopup();
        stopSpeech();
      }
    };

    createPopup();
    attachDocument(document);
    attachIframes();
    const observer = new MutationObserver(attachIframes);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('mousedown', dismissParent);
    document.addEventListener('touchstart', dismissParent);

    return () => {
      observer.disconnect();
      document.removeEventListener('mousedown', dismissParent);
      document.removeEventListener('touchstart', dismissParent);
      iframeLoadHandlers.forEach((handler, iframe) => {
        iframe.removeEventListener('load', handler);
      });
      iframeDocumentCleanups.forEach((cleanup) => cleanup());
      documentCleanups.forEach((cleanup) => cleanup());
      selectionTimers.forEach(window.clearTimeout);
      popup?.remove();
      stopSpeech();
    };
  }, []);

  return null;
};

export default ReadAloud;
